"use client";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js"; // UTC plugin
import timezone from "dayjs/plugin/timezone.js"; // Timezone plugin
import customParseFormat from "dayjs/plugin/customParseFormat";

import { useState, useMemo, useEffect } from "react";
import { MatchService } from "@/services/matchService";
import { MatchInfoProvider } from "./MatchInfoContext";
import MatchHeader from "./MatchHeader";
import TabNavigation from "./TabNavigation";
import LiveTab from "./LiveTab";
import HeadToHeadTab from "./HeadToHeadTab";
import RecentFormTab from "./RecentFormTab";
import OddsTab from "./OddTab";
import LoadingInner from "../Loading";
import { normalizeData } from "@/utils";

const TZ = "Asia/Ho_Chi_Minh";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export default function ThongTinTranDau({
  isOpen,
  onClose,
  match: initialMatch,
  matchInfo,
  statsInfo,
}) {
  const { isLive } = matchInfo;
  const [activeTab, setActiveTab] = useState(isLive ? "live" : "h2h");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setData(null);
      setLoading(false);
      setActiveTab("live");
      return;
    }
    if (!initialMatch?.id) return;
    let cancelled = false;
    setData(null);
    setLoading(true);
    const { id: matchId, homeId, awayId } = initialMatch;

    Promise.all([
      MatchService.getMatchById(matchId),
      MatchService.getMatchAnalysis(matchId),
      MatchService.getLiveOddByMatchId(matchId),
      MatchService.getOddByMatchId(matchId),
      MatchService.getMatchComparison(matchId),
      MatchService.getMatchGoalStats(matchId),
      MatchService.getMatchOddStats(matchId),
      MatchService.getMatchFormStats(matchId,`${homeId},${awayId}`),
      MatchService.getMatchLatest(matchId, homeId, awayId),
      MatchService.getMatchLineups(matchId, homeId, awayId),
      MatchService.getHeatMapData(isLive, {
        isLive,
        homeName: normalizeData(initialMatch?.homeTeam?.name),
        awayName: normalizeData(initialMatch?.awayTeam?.name),
        date: dayjs.unix(initialMatch?.matchTime).tz(TZ).format('YYYY-MM-DD'),
      }),

    ]).then(
      ([
        matchRes,
        matchAnalysis,
        liveOddData,
        oddData,
        matchComparison,
        matchGoalStats,
        matchOddStats,
        matchFormStats,
        matchLatest,
        matchLineups,
        heatMapLinkLive,
      ]) => {
        if (cancelled) return;
        const match =
          matchRes?.success && matchRes?.data ? matchRes.data : initialMatch;
        setData({
          match,
          matchAnalysis: matchAnalysis,
          liveOddData: liveOddData || {},
          oddData: oddData || {},
          matchComparison: matchComparison || {},
          matchGoalStats: matchGoalStats || {},
          matchOddStats: matchOddStats || {},
          matchFormStats: matchFormStats || {},
          matchLatest: matchLatest || {},
          lineup: matchLineups || {},
          heatMapLinkLive: heatMapLinkLive || null,
        });
      }
    ).catch(() => {
      if (!cancelled) setData({ match: initialMatch });
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, initialMatch?.id]);

  const contextValue = useMemo(
    () => ({
      match: data?.match ?? initialMatch,
      matchAnalysis: data?.matchAnalysis ?? null,
      liveOddData: data?.liveOddData ?? {},
      oddData: data?.oddData ?? {},
      matchComparison: data?.matchComparison ?? {},
      matchGoalStats: data?.matchGoalStats ?? {},
      matchOddStats: data?.matchOddStats ?? {},
      matchFormStats: data?.matchFormStats ?? {},
      matchLatest: data?.matchLatest ?? {},
      lineup: data?.lineup ?? {},
      heatMapLinkLive: data?.heatMapLinkLive ?? null,
      matchInfo,
      statsInfo,
      isLoading: loading,
    }),
    [data, initialMatch, matchInfo, statsInfo, loading]
  );

  if (!isOpen) return null;

  return (
    <MatchInfoProvider value={contextValue}>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div
          className="relative bg-[var(--theme-bg)] rounded-xl w-full sm:w-[800px] h-[85vh] sm:h-[800px] flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-[var(--theme-accent)] hover:bg-[var(--theme-primary)] text-[var(--theme-text)] hover:text-[var(--theme-on-primary)] transition-colors z-10"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M1 1L13 13M1 13L13 1" />
            </svg>
          </button>

          <div className="p-4 border-b border-[var(--theme-accent)] shrink-0 bg-[var(--theme-accent)]">
            <h2 className="text-lg font-semibold text-[var(--theme-text)] pr-8">Thông tin trận đấu</h2>
          </div>

          <div className="px-4 shrink-0">
            <MatchHeader />
            <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-4">
            {
              loading ? <LoadingInner /> : (
                <>
                  {activeTab === "live" && <LiveTab key={initialMatch?.id} />}
                  {activeTab === "h2h" && <HeadToHeadTab key={initialMatch?.id} />}
                  {activeTab === "form" && <RecentFormTab key={initialMatch?.id} />}
                  {activeTab === "odds" && <OddsTab key={initialMatch?.id} />}
                </>
              )
            }
          </div>
        </div>
      </div>
    </MatchInfoProvider>
  );
}
